/**
 * Day Mapper Utility
 *
 * Handles bidirectional mapping between:
 * - API format: English day keys ('Mon', 'Tue', etc.)
 * - UI format: Spanish day labels ('Lun.', 'Mar.', etc.)
 *
 * Used by AvailabilityConfigurationModal and related components.
 */
/** All day labels in order (Monday to Sunday) */
export const DAY_LABELS = [
    'Lun.', 'Mar.', 'Miér.', 'Jue.', 'Vie.', 'Sáb.', 'Dom.'
];
/** All day keys in order (Monday to Sunday) */
export const DAY_KEYS = [
    'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'
];
/** Mapping from Spanish labels to English keys */
const LABEL_TO_KEY = {
    'Lun.': 'Mon',
    'Mar.': 'Tue',
    'Miér.': 'Wed',
    'Jue.': 'Thu',
    'Vie.': 'Fri',
    'Sáb.': 'Sat',
    'Dom.': 'Sun',
};
/** Mapping from English keys to Spanish labels */
const KEY_TO_LABEL = {
    'Mon': 'Lun.',
    'Tue': 'Mar.',
    'Wed': 'Miér.',
    'Thu': 'Jue.',
    'Fri': 'Vie.',
    'Sat': 'Sáb.',
    'Sun': 'Dom.',
};
/** Full day names in Spanish for display */
const KEY_TO_FULL_NAME = {
    'Mon': 'Lunes',
    'Tue': 'Martes',
    'Wed': 'Miércoles',
    'Thu': 'Jueves',
    'Fri': 'Viernes',
    'Sat': 'Sábado',
    'Sun': 'Domingo',
};
export const dayMapper = {
    /**
     * Convert a Spanish day label to English key
     * @param label - Spanish day label (e.g., 'Lun.')
     * @returns English day key (e.g., 'Mon') or undefined if invalid
     */
    labelToKey(label) {
        return LABEL_TO_KEY[label];
    },
    /**
     * Convert an English day key to Spanish label
     * @param key - English day key (e.g., 'Mon')
     * @returns Spanish day label (e.g., 'Lun.') or undefined if invalid
     */
    keyToLabel(key) {
        return KEY_TO_LABEL[key];
    },
    /**
     * Convert an English day key to full Spanish name
     * @param key - English day key (e.g., 'Mon')
     * @returns Full Spanish name (e.g., 'Lunes')
     */
    keyToFullName(key) {
        return KEY_TO_FULL_NAME[key] ?? key;
    },
    /**
     * Convert an array of Spanish labels to English keys
     * @param labels - Array of Spanish day labels
     * @returns Array of English day keys (invalid entries filtered out)
     */
    labelsToKeys(labels) {
        return labels
            .map(label => LABEL_TO_KEY[label])
            .filter((key) => key !== undefined);
    },
    /**
     * Convert an array of English keys to Spanish labels
     * @param keys - Array of English day keys
     * @returns Array of Spanish day labels (invalid entries filtered out)
     */
    keysToLabels(keys) {
        return keys
            .map(key => KEY_TO_LABEL[key])
            .filter((label) => label !== undefined);
    },
    /**
     * Check if a string is a valid day label
     */
    isValidLabel(value) {
        return value in LABEL_TO_KEY;
    },
    /**
     * Check if a string is a valid day key
     */
    isValidKey(value) {
        return value in KEY_TO_LABEL;
    },
    /**
     * Get the index of a day (0 = Monday, 6 = Sunday)
     */
    getDayIndex(key) {
        return DAY_KEYS.indexOf(key);
    },
    /**
     * Format a range of days for display
     * @param startKey - First day key
     * @param endKey - Last day key
     * @returns Formatted string (e.g., 'Lunes a Viernes')
     */
    formatRange(startKey, endKey) {
        const startName = KEY_TO_FULL_NAME[startKey];
        const endName = KEY_TO_FULL_NAME[endKey];
        if (startKey === endKey) {
            return startName;
        }
        return `${startName} a ${endName.toLowerCase()}`;
    },
};
